<script setup lang="ts">
import type {
  VideoCallParticipant,
  VideoCallPeerDiagnostics,
} from "~/types/videoCalls";

const props = withDefaults(
  defineProps<{
    participants: VideoCallParticipant[];
    diagnostics: Record<string, VideoCallPeerDiagnostics | undefined>;
    peerStates?: Record<string, string | undefined>;
  }>(),
  {
    peerStates: () => ({}),
  },
);

const emit = defineEmits<{
  opened: [];
}>();

const menuOpen = ref(false);
const activatorElement = ref<HTMLButtonElement | null>(null);
const closeElement = ref<HTMLButtonElement | null>(null);
const copiedParticipantId = ref("");
let copiedTimer: ReturnType<typeof setTimeout> | null = null;

const routeLabel = (diagnostic?: VideoCallPeerDiagnostics): string => {
  if (!diagnostic || diagnostic.route === "unknown") {
    return "Coletando dados…";
  }

  if (diagnostic.route === "direct") {
    return "Conexão padrão";
  }

  return "Via servidor";
};

const connectionStateLabel = (state?: string): string => {
  const labels: Record<string, string> = {
    new: "Preparando",
    checking: "Conectando",
    connecting: "Conectando",
    connected: "Conectado",
    completed: "Conectado",
    disconnected: "Reconectando",
    reconnecting: "Reconectando",
    failed: "Falha na conexão",
    closed: "Encerrado",
  };

  return labels[state ?? ""] ?? "Coletando dados…";
};

const qualityLabel = (diagnostic?: VideoCallPeerDiagnostics): string => {
  const profiles = {
    focus: "Alta (720p)",
    standard: "Padrão",
    low: "Reduzida",
    survival: "Mínima",
    paused: "Vídeo pausado",
  } as const;
  const reasons = {
    default: "quantidade de participantes",
    receiver: "preferência do participante",
    visibility: "vídeo não visível",
    audio: "proteção do áudio",
    bandwidth: "largura de banda",
    cpu: "desempenho do dispositivo",
    recovery: "recuperação gradual",
  } as const;
  const profile = diagnostic?.qualityProfile;
  if (!profile) return "Aguardando leitura";
  const reason = diagnostic.qualityReason
    ? reasons[diagnostic.qualityReason]
    : null;
  return reason ? `${profiles[profile]} · ${reason}` : profiles[profile];
};

const audioHealthLabel = (diagnostic?: VideoCallPeerDiagnostics): string => {
  const labels = {
    healthy: "Estável",
    warning: "Em atenção",
    critical: "Crítico",
    unknown: "Aguardando leitura",
  } as const;
  return labels[diagnostic?.audioHealth ?? "unknown"];
};

const metricLabel = (
  value: number | null | undefined,
  suffix: string,
  fractionDigits = 0,
): string => {
  if (typeof value !== "number" || !Number.isFinite(value)) {
    return "Aguardando leitura";
  }

  return `${value.toFixed(fractionDigits)} ${suffix}`;
};

const collectedAtLabel = (value?: string | null): string => {
  if (!value) return "Aguardando leitura";

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "Aguardando leitura";

  return new Intl.DateTimeFormat("pt-BR", {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  }).format(date);
};

const buildCopyText = (participant: VideoCallParticipant): string => {
  const diagnostic = props.diagnostics[participant.id];
  const state = props.peerStates[participant.id];

  return [
    "Diagnóstico da chamada TeachPlus",
    `Participante: ${participant.name}`,
    `Rota: ${routeLabel(diagnostic)}`,
    `Estado: ${connectionStateLabel(state)}`,
    `Latência: ${metricLabel(diagnostic?.roundTripTimeMs, "ms")}`,
    `Perda de pacotes: ${metricLabel(diagnostic?.packetLossPercent, "%", 1)}`,
    `Qualidade de vídeo: ${qualityLabel(diagnostic)}`,
    `Saúde do áudio: ${audioHealthLabel(diagnostic)}`,
    `Leitura: ${collectedAtLabel(diagnostic?.collectedAt)}`,
  ].join("\n");
};

const copyText = async (value: string): Promise<void> => {
  try {
    await navigator.clipboard.writeText(value);
  } catch {
    const input = document.createElement("textarea");
    input.value = value;
    input.style.position = "fixed";
    input.style.opacity = "0";
    document.body.appendChild(input);
    input.select();
    document.execCommand("copy");
    input.remove();
  }
};

const copyDiagnostic = async (
  participant: VideoCallParticipant,
): Promise<void> => {
  await copyText(buildCopyText(participant));
  copiedParticipantId.value = participant.id;

  if (copiedTimer) clearTimeout(copiedTimer);
  copiedTimer = setTimeout(() => {
    copiedParticipantId.value = "";
  }, 2_000);
};

watch(menuOpen, async (isOpen, wasOpen) => {
  if (isOpen) {
    emit("opened");
    await nextTick();
    closeElement.value?.focus();
    return;
  }

  if (wasOpen) {
    await nextTick();
    activatorElement.value?.focus();
  }
});

onBeforeUnmount(() => {
  if (copiedTimer) clearTimeout(copiedTimer);
});
</script>

<template>
  <v-menu
    v-model="menuOpen"
    :close-on-content-click="false"
    :min-width="0"
    :max-width="380"
    :z-index="2500"
    location="bottom end"
    offset="8"
  >
    <template #activator="{ props: activatorProps }">
      <button
        v-bind="activatorProps"
        ref="activatorElement"
        class="connection-diagnostics__trigger"
        type="button"
        aria-label="Ver dados da conexão"
        aria-haspopup="dialog"
        title="Ver dados da conexão"
        data-testid="video-call-connection-diagnostics-trigger"
      >
        <i class="mdi mdi-signal" aria-hidden="true" />
      </button>
    </template>

    <section
      class="connection-diagnostics__panel"
      role="dialog"
      aria-label="Dados da conexão"
      data-testid="video-call-connection-diagnostics"
    >
      <header>
        <div>
          <span>Chamada</span>
          <h3>Dados da conexão</h3>
        </div>
        <button
          ref="closeElement"
          type="button"
          aria-label="Fechar dados da conexão"
          @click="menuOpen = false"
        >
          <i class="mdi mdi-close" aria-hidden="true" />
        </button>
      </header>

      <div class="connection-diagnostics__participants">
        <article
          v-for="participant in props.participants"
          :key="participant.id"
          class="connection-diagnostics__participant"
          :data-participant-id="participant.id"
        >
          <div class="connection-diagnostics__identity">
            <strong>{{ participant.name }}</strong>
            <span
              v-if="props.diagnostics[participant.id]?.degraded"
              class="connection-diagnostics__degraded"
            >
              Conexão instável
            </span>
          </div>

          <p class="connection-diagnostics__route">
            {{ routeLabel(props.diagnostics[participant.id]) }}
          </p>

          <dl>
            <div>
              <dt>Estado</dt>
              <dd>
                {{ connectionStateLabel(props.peerStates[participant.id]) }}
              </dd>
            </div>
            <div>
              <dt>Latência</dt>
              <dd>
                {{
                  metricLabel(
                    props.diagnostics[participant.id]?.roundTripTimeMs,
                    "ms",
                  )
                }}
              </dd>
            </div>
            <div>
              <dt>Perda de pacotes</dt>
              <dd>
                {{
                  metricLabel(
                    props.diagnostics[participant.id]?.packetLossPercent,
                    "%",
                    1,
                  )
                }}
              </dd>
            </div>
            <div>
              <dt>Qualidade de vídeo</dt>
              <dd>{{ qualityLabel(props.diagnostics[participant.id]) }}</dd>
            </div>
            <div>
              <dt>Saúde do áudio</dt>
              <dd>{{ audioHealthLabel(props.diagnostics[participant.id]) }}</dd>
            </div>
            <div>
              <dt>Leitura</dt>
              <dd>
                {{
                  collectedAtLabel(
                    props.diagnostics[participant.id]?.collectedAt,
                  )
                }}
              </dd>
            </div>
          </dl>

          <button
            type="button"
            class="connection-diagnostics__copy"
            :aria-label="`Copiar diagnóstico de ${participant.name}`"
            @click="copyDiagnostic(participant)"
          >
            <i
              class="mdi"
              :class="
                copiedParticipantId === participant.id
                  ? 'mdi-check'
                  : 'mdi-content-copy'
              "
              aria-hidden="true"
            />
            {{
              copiedParticipantId === participant.id
                ? "Diagnóstico copiado"
                : "Copiar diagnóstico"
            }}
          </button>
        </article>
      </div>

      <p class="connection-diagnostics__privacy">
        O diagnóstico não inclui IP, portas ou credenciais da chamada.
      </p>
    </section>
  </v-menu>
</template>

<style scoped lang="scss">
.connection-diagnostics__trigger {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 36px;
  width: 36px;
  height: 36px;
  border: 0;
  border-radius: 10px;
  background: #f0f3f9;
  color: #273550;
  cursor: pointer;

  &:focus-visible {
    outline: 3px solid rgba(49, 86, 255, 0.28);
    outline-offset: 2px;
  }
}

.connection-diagnostics__panel {
  width: min(380px, calc(100vw - 32px));
  max-height: min(520px, calc(100dvh - 32px));
  overflow: auto;
  border: 1px solid #dbe1ef;
  border-radius: 18px;
  background: #fff;
  box-shadow: 0 22px 60px rgba(17, 30, 55, 0.24);
  color: #26334d;

  > header {
    display: flex;
    position: sticky;
    z-index: 1;
    top: 0;
    align-items: center;
    justify-content: space-between;
    gap: 16px;
    padding: 16px;
    border-bottom: 1px solid #e8ecf4;
    background: #fff;

    span {
      color: #718098;
      font-size: 0.66rem;
      font-weight: 700;
      letter-spacing: 0.08em;
      text-transform: uppercase;
    }

    h3 {
      margin: 2px 0 0;
      color: #17233c;
      font-size: 1rem;
    }

    button {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      flex: 0 0 36px;
      width: 36px;
      height: 36px;
      border: 0;
      border-radius: 10px;
      background: #f0f3f9;
      color: #526078;
      cursor: pointer;
    }
  }
}

.connection-diagnostics__participants {
  display: grid;
  gap: 10px;
  padding: 12px;
}

.connection-diagnostics__participant {
  padding: 12px;
  border: 1px solid #e2e7f1;
  border-radius: 12px;
  background: #f9faff;

  dl {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 8px 12px;
    margin: 12px 0;

    div {
      min-width: 0;
    }

    dt {
      color: #718098;
      font-size: 0.66rem;
    }

    dd {
      margin: 2px 0 0;
      overflow-wrap: anywhere;
      color: #26334d;
      font-size: 0.75rem;
      font-weight: 700;
    }
  }
}

.connection-diagnostics__identity {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 8px;

  strong {
    min-width: 0;
    overflow: hidden;
    color: #17233c;
    font-size: 0.82rem;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
}

.connection-diagnostics__degraded {
  flex: 0 0 auto;
  padding: 3px 7px;
  border-radius: 999px;
  background: #fff0df;
  color: #8a4e00;
  font-size: 0.62rem;
  font-weight: 700;
}

.connection-diagnostics__route {
  margin: 6px 0 0;
  color: #3156ff;
  font-size: 0.76rem;
  font-weight: 700;
}

.connection-diagnostics__copy {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  min-height: 34px;
  padding: 6px 10px;
  border: 1px solid #3156ff;
  border-radius: 9px;
  background: #fff;
  color: #3156ff;
  font: inherit;
  font-size: 0.72rem;
  font-weight: 700;
  cursor: pointer;
}

.connection-diagnostics__privacy {
  margin: 0;
  padding: 0 16px 16px;
  color: #718098;
  font-size: 0.66rem;
  line-height: 1.4;
}

@media (max-width: 479px) {
  .connection-diagnostics__participant dl {
    grid-template-columns: 1fr;
  }

  .connection-diagnostics__copy {
    width: 100%;
  }
}
</style>
